import styles from './PublicScheduling.module.scss';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Loader } from '@spotted-zebra-uk/ui-components';
import {
  BookingConfirmed,
  BookingDetails,
  BookingResultType,
  ConfirmSlotResult,
  HoldAccepted,
  HoldSlotResult,
} from './api/bookingApi.types';
import {
  BookingFailure,
  confirmSlot,
  declineAll,
  fetchBooking,
  holdSlot,
} from './api/bookingOperations';
import { BookingRefusalCode } from './api/bookingTransport';
import BookedFlow from './components/BookedFlow/BookedFlow';
import DeclinedState from './components/DeclinedState/DeclinedState';
import InterviewCard from './components/InterviewCard/InterviewCard';
import StateMessage from './components/StateMessage/StateMessage';
import {
  SchedulingOffer,
  SchedulingPageState,
  SchedulingSlot,
} from './scheduling.types';
import {
  formatDayFull,
  formatSlotRange,
  getBrowserTimeZone,
} from './timezone';
import { usePublicSchedulingOffer } from './usePublicSchedulingOffer';

type Step = 'pick' | 'booked' | 'declining' | 'declined';

/** What either booking call answers when it does not book. */
type BookingRefusal = Exclude<
  HoldSlotResult | ConfirmSlotResult,
  HoldAccepted | BookingConfirmed
>;

/**
 * Candidate booking page behind the public, login-free link (BL-1513).
 * Renders outside the authenticated provider tree, so it has no
 * HelmetProvider or LiveAnnouncer: the title and announcements are set here.
 *
 * Booking is one step (BL-1608): the chosen time is held and confirmed back
 * to back in the same action, so the candidate never sees the server's hold.
 */
const PublicScheduling: FC = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const { state, reload } = usePublicSchedulingOffer(token);

  const [step, setStep] = useState<Step>('pick');
  const [timeZone, setTimeZone] = useState(getBrowserTimeZone);
  /** The booking just made on this page, read back so it shows what a reopened link shows. */
  const [bookedDetails, setBookedDetails] = useState<BookingDetails | null>(
    null
  );
  const [busy, setBusy] = useState(false);
  /** Shown in the pick step after something went wrong with a booking. */
  const [notice, setNotice] = useState<string | null>(null);
  /** Shown on the decline screen after a call there failed. */
  const [stepNotice, setStepNotice] = useState<string | null>(null);
  /**
   * A terminal answer received mid-flow (the link expired or stopped
   * resolving while the candidate was booking), which outranks whatever the
   * initial read returned.
   */
  const [override, setOverride] = useState<SchedulingPageState | null>(null);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    document.title = t('publicScheduling.pageTitle');
  }, [t]);

  /**
   * Starts the page over from the link's read flow, for a booking that
   * changed under the candidate. Clears what this page had decided so the
   * fresh read is what shows.
   */
  const restart = useCallback(() => {
    setOverride(null);
    setBookedDetails(null);
    setStepNotice(null);
    setNotice(null);
    setStep('pick');
    reload();
  }, [reload]);

  /** Sends the candidate back to the times with a reason, refreshed. */
  const backToPickWith = useCallback(
    (reason: string) => {
      setStepNotice(null);
      setStep('pick');
      setNotice(reason);
      setAnnouncement(reason);
      reload();
    },
    [reload]
  );

  /**
   * Refusals that end the page rather than the step. Returns whether the
   * failure was handled here; the caller shows its own notice otherwise.
   */
  const applyTerminalFailure = (failure: BookingFailure): boolean => {
    if (failure.kind === 'refused' && failure.code === BookingRefusalCode.NOT_FOUND) {
      setOverride({ kind: 'notFound' });
      return true;
    }
    return false;
  };

  /**
   * Books the chosen time: holds the seat, then confirms it straight away.
   * Both calls answer the same refusals, which are handled the same way; a
   * call that cannot be made leaves the candidate on the times with their
   * selection kept, so they can try again.
   *
   * `timezone` is the zone the candidate viewed and picked the time in;
   * `candidateTimezone` is the zone their device reports. The offer carries
   * no interview zone of its own, so the picked zone is the only one the
   * page can record the booking in.
   */
  const book = async (slot: SchedulingSlot) => {
    if (!token) {
      return;
    }
    setBusy(true);
    try {
      const held = await holdSlot(token, slot.seatId);
      if (!held.ok) {
        if (!applyTerminalFailure(held.failure)) {
          setNotice(t('publicScheduling.bookingFailedNotice'));
        }
        return;
      }
      if (held.result.__typename !== BookingResultType.HOLD_ACCEPTED) {
        applyRefusal(held.result);
        return;
      }

      const confirmed = await confirmSlot(token, {
        seatId: slot.seatId,
        timezone: timeZone,
        candidateTimezone: getBrowserTimeZone(),
      });
      if (!confirmed.ok) {
        if (!applyTerminalFailure(confirmed.failure)) {
          setNotice(t('publicScheduling.bookingFailedNotice'));
        }
        return;
      }
      if (confirmed.result.__typename === BookingResultType.BOOKING_CONFIRMED) {
        await showBookedPage(slot);
        return;
      }
      applyRefusal(confirmed.result);
    } finally {
      setBusy(false);
    }
  };

  /**
   * A refusal from either booking call. The hold was taken a moment before
   * the confirm, so a hold that has already run out is, to the candidate,
   * a time somebody else got to first.
   */
  const applyRefusal = (result: BookingRefusal) => {
    switch (result.__typename) {
      case BookingResultType.SEAT_UNAVAILABLE:
      case BookingResultType.HOLD_EXPIRED:
        backToPickWith(t('publicScheduling.seatTakenNotice'));
        return;
      case BookingResultType.INVALID_TIMEZONE:
        backToPickWith(t('publicScheduling.timezoneRejectedNotice'));
        return;
      case BookingResultType.EXPIRED_LINK_STATE:
        setOverride({ kind: 'expired', expiredAt: result.expiredAt });
        return;
      case BookingResultType.LINK_REPLACED_STATE:
        setOverride({ kind: 'replaced' });
        return;
    }
  };

  /**
   * Reads the booking just confirmed and shows it, so the first view is the
   * same page a reopened link gives. When it cannot be read the page reloads,
   * which reads it again and says what it can.
   */
  const showBookedPage = async (slot: SchedulingSlot) => {
    const call = token ? await fetchBooking(token) : null;
    if (
      call?.ok &&
      call.result.__typename === BookingResultType.BOOKING_DETAILS
    ) {
      setBookedDetails(call.result);
      setNotice(null);
      setStepNotice(null);
      setStep('booked');
      setAnnouncement(
        t('publicScheduling.announceBooked', {
          day: formatDayFull(slot.startsAt, timeZone),
          range: formatSlotRange(slot, timeZone),
        })
      );
      return;
    }
    backToPickWith(t('publicScheduling.bookedReadFailedNotice'));
  };

  /**
   * @param cancelsInterview - Whether this declines a reschedule offer, which
   * cancels the interview it would have moved.
   */
  const decline = async (reason: string, cancelsInterview: boolean) => {
    if (!token) {
      return;
    }
    setBusy(true);
    try {
      const reasonText = reason.trim();
      const call = await declineAll(
        token,
        reasonText === '' ? undefined : reasonText
      );
      if (!call.ok) {
        if (!applyTerminalFailure(call.failure)) {
          setStepNotice(t('publicScheduling.declineFailedNotice'));
        }
        return;
      }
      switch (call.result.__typename) {
        case BookingResultType.DECLINE_RECORDED:
          setStepNotice(null);
          setStep('declined');
          setAnnouncement(
            cancelsInterview
              ? t('publicScheduling.announceRescheduleDeclined')
              : t('publicScheduling.announceDeclined')
          );
          return;
        case BookingResultType.DECLINE_REASON_TOO_LONG:
          setStepNotice(
            t('publicScheduling.declineTooLongNotice', {
              maxLength: call.result.maxLength,
            })
          );
          return;
        case BookingResultType.SEAT_UNAVAILABLE:
          setStepNotice(t('publicScheduling.declineClosedNotice'));
          return;
        case BookingResultType.EXPIRED_LINK_STATE:
          setOverride({ kind: 'expired', expiredAt: call.result.expiredAt });
          return;
        case BookingResultType.LINK_REPLACED_STATE:
          setOverride({ kind: 'replaced' });
          return;
      }
    } finally {
      setBusy(false);
    }
  };

  const renderOffer = (offer: SchedulingOffer) => {
    switch (step) {
      case 'booked':
        return bookedDetails && token ? (
          <BookedFlow
            booking={bookedDetails}
            token={token}
            timeZone={timeZone}
            focusHeading
            onTerminal={setOverride}
            onReload={restart}
            announce={setAnnouncement}
          />
        ) : null;
      case 'declining':
        return (
          <DeclinedState
            sending={busy}
            notice={stepNotice}
            cancelWarning={
              offer.currentInterview
                ? t('publicScheduling.rescheduleDeclineWarning', {
                    day: formatDayFull(offer.currentInterview.startsAt, timeZone),
                    range: formatSlotRange(offer.currentInterview, timeZone),
                  })
                : undefined
            }
            onSend={reason => decline(reason, offer.currentInterview !== null)}
            onBack={() => {
              setStepNotice(null);
              setStep('pick');
            }}
          />
        );
      case 'declined':
        return (
          <StateMessage
            title={
              offer.currentInterview
                ? t('publicScheduling.rescheduleDeclinedSent')
                : t('publicScheduling.declinedSent')
            }
            body={t('publicScheduling.declinedSentBody')}
            focusTitle
          />
        );
      default:
        return (
          <InterviewCard
            offer={offer}
            timeZone={timeZone}
            onTimeZoneChange={setTimeZone}
            notice={notice}
            booking={busy}
            onBook={book}
            onDeclineAll={() => {
              setStepNotice(null);
              setStep('declining');
            }}
          />
        );
    }
  };

  const renderBody = () => {
    const effective = override ?? state;
    switch (effective.kind) {
      case 'loading':
        return (
          <div className={styles.loader}>
            <Loader variant="bubbles" />
          </div>
        );
      case 'ready':
        return renderOffer(effective.offer);
      case 'booked':
        return token ? (
          <BookedFlow
            booking={effective.booking}
            token={token}
            timeZone={effective.booking.candidateTimezone || timeZone}
            onTerminal={setOverride}
            onReload={restart}
            announce={setAnnouncement}
          />
        ) : null;
      case 'expired':
        return (
          <StateMessage
            title={t('publicScheduling.linkExpiredTitle')}
            body={t('publicScheduling.linkExpiredBody')}
          />
        );
      case 'replaced':
        return (
          <StateMessage
            title={t('publicScheduling.linkReplacedTitle')}
            body={t('publicScheduling.linkReplacedBody')}
          />
        );
      case 'noSlots':
        return (
          <StateMessage
            title={t('publicScheduling.noSlotsTitle')}
            body={t('publicScheduling.noSlotsBody')}
          />
        );
      case 'closed':
        return (
          <StateMessage
            title={t('publicScheduling.closedTitle')}
            body={t('publicScheduling.closedBody')}
          />
        );
      case 'declined':
        return (
          <StateMessage
            title={t('publicScheduling.declinedTitle')}
            body={t('publicScheduling.declinedBody')}
          />
        );
      case 'notFound':
        return <StateMessage title={t('publicScheduling.notFound')} />;
      case 'rateLimited':
        return (
          <StateMessage
            title={t('publicScheduling.rateLimitedTitle')}
            body={t('publicScheduling.rateLimitedBody')}
            action={{ label: t('publicScheduling.tryAgain'), onClick: reload }}
          />
        );
      case 'error':
        return (
          <StateMessage
            title={t('publicScheduling.errorTitle')}
            body={t('publicScheduling.errorBody')}
            action={{ label: t('publicScheduling.tryAgain'), onClick: reload }}
          />
        );
    }
  };

  return (
    <main className={styles.main}>
      {renderBody()}
      <div className={styles.liveRegion} aria-live="polite" role="status">
        {announcement}
      </div>
    </main>
  );
};

export default PublicScheduling;
